import { apiErrorMessage, isForbidden } from '@agos/api-client'
import type { BeverageStockItem } from '@agos/api-client/model'
import {
  getGetBeverageStockItemsQueryKey,
  getGetBeverageStockSummaryQueryKey,
  useGetBeverageStockItems,
  useGetBeverageStockSummary,
  usePostBeverageStockReceipts,
} from '@agos/api-client/inventory'
import { T, toDateParam, tt, useModalFocus } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  DataTable,
  DateRangePicker,
  FilterBar,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, Plus, RefreshCw, Wine, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { BeverageCategoriesPanel } from './BeverageCategoriesPanel'
import { serverAnswer } from './read-failure'
import { drinkOption, levelNote, quantityLabel, receiptAmount, receivedNote } from './receive'
import {
  BEVERAGE_TABS,
  beverageTotals,
  filterRows,
  formatQty,
  stockBadge,
  tabCounts,
  tabCoverage,
  type BeverageRow,
  type BeverageTab,
} from './beverage-tabs'

/**
 * `/beverage-stock` - what was received, what was poured, and what is left.
 *
 * Legacy's layout: a date range you apply, six category tabs, a search box, the
 * summary table and a *Zaznamenať príjem* modal.
 *
 * **FB-079** is the tab that never worked. Legacy looks for `capovane` - the neuter
 * ending - while both draught products in the data are *Kofola čapovaná* and
 * *Krušovice čapovaný*. Matching the stem finds them, and the tab counts are shown so
 * a tab that holds nothing is visible rather than silently empty.
 *
 * Ported from legacy `pages/beverage-stock.tsx`.
 *
 * **§9.743, owner O116** - *"how does it know in which category it supposed to be?"*. It guessed from names,
 * twice, and said nothing; the *Kategórie* panel shows each category with the guess and takes a choice.
 */

function today(): string {
  return toDateParam(new Date())
}

export function BeverageStockPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<BeverageTab>('all')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [appliedFrom, setAppliedFrom] = useState(today)
  const [appliedTo, setAppliedTo] = useState(today)
  const [receiving, setReceiving] = useState(false)

  // Legacy's own window: the whole of both days, so an evening pour on the last day is inside the range.
  // §9.748 - to the last second: `T23:59` left the day's last minute out. Both ends are read in Bratislava's
  // time on the server (BACKEND.md §10A.400).
  const { data, isLoading, isError, error, refetch } = useGetBeverageStockSummary({
    from: `${appliedFrom}T00:00`,
    to: `${appliedTo}T23:59:59`,
  })

  // §9.748 - typed as it is now: `noStockItems` was declared a list of objects and cast here by hand, and that
  // declaration was Sereď's 500 (BACKEND.md §10A.401).
  const rows = useMemo<BeverageRow[]>(() => data?.rows ?? [], [data])
  const noStockItems = data?.noStockItems ?? []

  const shown = useMemo(() => filterRows(rows, tab, search), [rows, tab, search])
  const counts = tabCounts(rows)
  const totals = beverageTotals(rows)
  const coverage = tabCoverage(rows)

  const columns: Column<BeverageRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Surovina / Nápoj'),
        sortable: true,
        sortValue: (row) => row.stockName,
        // §9.269 - `truncate` came off. On an `inline` span `overflow` does not apply, so of its
        // three declarations only `white-space: nowrap` took effect - cancelling the `break-words`
        // the phone card asks for and running *Captain Morgan Spiced Gold Rum 35 % 0,4ml* to 449 px
        // inside a 224 px box. It was equally inert in the desktop table, where no name was being
        // shortened by it, so nothing there changes.
        render: (row) => <span className="font-semibold">{row.stockName}</span>,
      },
      {
        key: 'category',
        header: tt('Kategória'),
        sortable: true,
        sortValue: (row) => row.category ?? '',
        render: (row) =>
          row.category ? (
            <StatusBadge label={row.category} color="amber" />
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        key: 'receipt',
        header: tt('Príjem'),
        sortable: true,
        sortValue: (row) => row.receipt ?? 0,
        className: 'text-right font-bold text-emerald-600',
        headerClassName: 'text-right',
        render: (row) =>
          (row.receipt ?? 0) > 0 ? `+${formatQty(row.receipt, row.unit)}` : '-',
      },
      {
        key: 'sales',
        header: tt('Predaj'),
        sortable: true,
        sortValue: (row) => row.sales ?? 0,
        className: 'text-right font-bold text-red-500',
        headerClassName: 'text-right',
        render: (row) => ((row.sales ?? 0) > 0 ? `-${formatQty(row.sales, row.unit)}` : '-'),
      },
      {
        key: 'stockQty',
        header: tt('Zostatok'),
        sortable: true,
        sortValue: (row) => row.stockQty ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (row) => {
          const badge = stockBadge(row.stockQty)
          return (
            <div className="flex items-center justify-end gap-2">
              <span
                className={cn(
                  'font-bold text-base tabular-nums',
                  (row.stockQty ?? 0) < 0 && 'text-red-500',
                )}
              >
                {formatQty(row.stockQty, row.unit)}
              </span>
              <StatusBadge label={badge.label} color={badge.color} />
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Nápojový sklad')}
        description={tt('Sledovanie pohybov nápojov - príjem, predaj a zostatok')}
        icon={<Wine className="w-5 h-5" />}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať nápoj alebo kategóriu...'),
        }}
        filters={
          <>
            <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
            <button
              type="button"
              onClick={() => {
                setAppliedFrom(from)
                setAppliedTo(to)
              }}
              className={cn('flex items-center gap-2 px-5 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <RefreshCw className="w-4 h-4" />
              <T>Použiť</T>
            </button>
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => setReceiving(true)}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <T>Zaznamenať príjem</T>
          </button>
        }
      />

      {/* FB-820, §9.541 - **one gate at the read, not seven at the blocks.** This read feeds four cards and three
          count-gated warnings, and each block is correct about itself: hiding at zero is the right design. What was
          wrong is that the zero was fabricated - *V minuse* drew **0 in green** on the card that counts stock gone
          negative, and `noStockItems` read as *every drink is linked*. Threading the flag seven times would leave
          the cards to be forgotten; one branch covers all of them by construction. */}
      {isError ? (
        <ReadFailedBlock
          marker="beverage"
          title={tt('Prehľad nápojov sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          {/* §9.743 - a refusal says which: the screen's right, or the package. Sereď's banner was a 403 that
              reached no log, and this sentence is what would have named it. */}
          {isForbidden(error) ? (
            <span className="block font-semibold" data-beverage-refused>
              {apiErrorMessage(error, tt('Nemáte oprávnenie'))}
            </span>
          ) : (
            /* §9.746, owner O118 - every other failure names the server's answer too. Sereď's banner had no
               refusal line, so it was not the 403 §9.743 described, and nothing here said what it was. */
            <span className="block font-semibold" data-beverage-failure-detail>
              {serverAnswer(error)}
            </span>
          )}
          <T>Nuly by neboli skutočné - ani to, že žiadny nápoj nie je v minuse alebo bez suroviny.</T>
        </ReadFailedBlock>
      ) : null}

      {isError ? null : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-beverage-totals>
        <StatCard label={tt('Nápojov')} value={String(totals.rows)} icon={Wine} color="blue" />
        <StatCard label={tt('Predávalo sa')} value={String(totals.salesTotal)} icon={Wine} color="violet" />
        <StatCard
          label={tt('Vypredaných')}
          value={String(totals.empty)}
          icon={Wine}
          color={totals.empty > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('V minuse')}
          value={String(totals.negative)}
          icon={AlertTriangle}
          color={totals.negative > 0 ? 'red' : 'emerald'}
        />
      </div>

      {totals.negative > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-negative-note>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <T>
            Záporný zostatok znamená, že sa vydalo viac, než bolo prijaté - buď chýba príjem, alebo sa
            nápoj účtoval z inej suroviny.
          </T>

        </p>
      )}

      {coverage.unplaced > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{coverage.unplaced}</strong>{' '}
            <T>
              nápojov nepatrí do žiadnej kategórie v záložkách - nájdete ich pod „Všetky". Záložku im nastavíte v
              časti Kategórie.
            </T>
          </span>
        </p>
      )}

      {noStockItems.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-no-stock-items>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            {/* §9.743 - these are deliveries, not sales: a product received under a name no stock item has. */}
            <T>Tieto nápoje sa prijali, ale žiadna skladová položka sa nevolá rovnako - ich príjem preto tabuľka neukáže:</T>{' '}
            {noStockItems.join(', ')}
          </span>
        </p>
      )}

      </>
      )}

      {/* §9.746, owner O118 - outside the summary's gate: the panel reads its own call, so a category can be
          set while the stock take cannot be read. Inside the gate it vanished with the summary at Sereď. */}
      <BeverageCategoriesPanel />

      {/* Counts on every tab, so FB-079's empty tab could not have hidden. */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {BEVERAGE_TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            aria-pressed={tab === entry.key}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap',
              tab === entry.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {entry.label}
            <span className="ml-1.5 text-[10px] font-normal opacity-70">{counts[entry.key]}</span>
          </button>
        ))}
      </div>

      {isError || isLoading ? (
        isError ? null : (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
        )
      ) : (
        <div data-beverage-rows>
          <DataTable<BeverageRow>
            columns={columns}
            data={shown}
            rowKey={(row) => row.stockItemId ?? 0}
            pageSize={25}
            emptyMessage={tt('Žiadne položky v danom rozsahu')}
            emptyIcon={<Wine className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {receiving && (
        <ReceiveDialog
          onClose={() => setReceiving(false)}
          onSaved={() => {
            setReceiving(false)
            // §9.748 - the row's Príjem and Zostatok, and the dialog's own levels, both changed.
            void queryClient.invalidateQueries({
              queryKey: getGetBeverageStockSummaryQueryKey(),
            })
            void queryClient.invalidateQueries({ queryKey: getGetBeverageStockItemsQueryKey() })
          }}
        />
      )}
    </div>
  )
}


/**
 * *Zaznamenať príjem* - FRONTEND.md §9.748, owner O122: *"ked dám "Zaznamenať príjem", tak to reálne nepridá"*.
 *
 * It offered products and wrote a delivery that moved no stock. It offers the stock take's own drinks now and
 * receives onto the one chosen, in that drink's unit; the toast says the level it brought the shelf to.
 */
function ReceiveDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast()
  const [stockItemId, setStockItemId] = useState('')
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')

  const { data, isLoading, isError, error, refetch } = useGetBeverageStockItems()
  const drinks = useMemo<BeverageStockItem[]>(() => data ?? [], [data])
  const chosen = drinks.find((drink) => String(drink.stockItemId) === stockItemId)

  const record = usePostBeverageStockReceipts({
    mutation: {
      onSuccess: (written) => {
        toast({ title: tt('Príjem zaznamenaný'), description: receivedNote(written) })
        onSaved()
      },
      // §9.385 - a receipt the server refused (a quantity of nothing, a drink the stock take does not count).
      onError: (caught) =>
        toast({
          title: tt('Chyba'),
          description: apiErrorMessage(caught, tt('Príjem sa nepodarilo zaznamenať.')),
          variant: 'destructive',
        }),
    },
  })

  const amount = receiptAmount(qty)
  const ready = chosen !== undefined && amount !== null

  /* §9.494, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card inside (§9.469). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tt('Zaznamenať príjem')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-receive-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            <T>Zaznamenať príjem</T>
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {isError ? (
            /* The drinks are absent, not none: an empty select would read as *there is nothing to receive*. */
            <div className="space-y-2 text-xs" role="alert" data-receive-drinks-failed>
              <p className="font-semibold text-red-500">
                <T>Nápoje sa nepodarilo načítať.</T>
              </p>
              <p className="text-muted-foreground" data-receive-drinks-failure-detail>
                {serverAnswer(error)}
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 font-semibold hover:bg-accent"
                data-receive-drinks-retry
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <T>Skúsiť znova</T>
              </button>
            </div>
          ) : (
            <label className="block space-y-1.5">
              <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                {tt('Nápoj')}
              </span>
              <select
                value={stockItemId}
                onChange={(event) => setStockItemId(event.target.value)}
                aria-label={tt('Nápoj')}
                disabled={isLoading}
                className={INPUT}
                data-receive-drink
              >
                <option value="">{isLoading ? tt('Načítavam nápoje…') : tt('- Vyberte nápoj -')}</option>
                {drinks.map((drink) => (
                  <option key={drink.stockItemId} value={String(drink.stockItemId)}>
                    {drinkOption(drink)}
                  </option>
                ))}
              </select>
              <span className="block text-xs text-muted-foreground" data-receive-drinks-rule>
                <T>
                  Ponúka skladové položky, ktoré Nápojový sklad počíta. Ak nápoj chýba, nemá skladovú položku
                  (pridáte ju v Sklade), alebo jeho skladová kategória nie je nastavená ako nápoj (v časti
                  Kategórie).
                </T>
              </span>
            </label>
          )}

          {chosen && (
            <p className="text-xs font-semibold tabular-nums" data-receive-level>
              {levelNote(chosen)}
            </p>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium" data-receive-qty-label>
              {quantityLabel(chosen?.unit)}
            </span>
            <input
              value={qty}
              onChange={(event) => setQty(event.target.value)}
              aria-label={tt('Množstvo príjmu')}
              inputMode="decimal"
              className={INPUT}
              placeholder="0"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Poznámka')}
            </span>
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              aria-label={tt('Poznámka k príjmu')}
              className={INPUT}
              placeholder={tt('Napr. faktúra 2026/119')}
            />
          </label>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() => {
              if (chosen === undefined || amount === null) return
              record.mutate({
                data: { stockItemId: chosen.stockItemId, qty: amount, note: note.trim() || null },
              })
            }}
            disabled={!ready || record.isPending}
            className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
            data-receive-save
          >
            {record.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Zaznamenať</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'
